import React, { useState, useEffect, useCallback } from 'react';
import { withFirebase } from '../../Firebase';
import Select from 'react-select';
import { Button, FormGroup, Form, Input } from 'reactstrap';
import ModalConsumer from '../../utility/Modal/ModalConsumer';
import GenericConfirmModal from '../../GenericModal/ConfirmModal';
import { doc, writeBatch, updateDoc } from 'firebase/firestore';
import { collection, collectionGroup, query, getDocs, onSnapshot, orderBy } from 'firebase/firestore';
import { tagMappingService } from '../../../services/tagMappingService';
import { getOrgCollectionPath, isOrgScopedCollection, isGlobalCollection, ORGANIZATIONSPATH } from '../../../constants/paths';
import { useOrganizationStore } from '../../../store/organization.store';
// Using new toast styling system from _toasts.scss

const FieldEditor = ({ collectionName, keyName, type, firebase }) => {
  const [options, setOptions] = useState([]);
  const [selectedFieldValue, setSelectedFieldValue] = useState('');
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processStatus, setProcessStatus] = useState('');
  const [toast, setToast] = useState({ show: false, message: '' });
  const currentOrgId = useOrganizationStore((state) => state.currentOrgId);

  // Helper to get the correct collection path based on whether it's org-scoped
  const getCollectionPath = useCallback((collName) => {
    if (currentOrgId && isOrgScopedCollection(collName)) {
      return getOrgCollectionPath(currentOrgId, collName);
    }
    return collName;
  }, [currentOrgId]);

  useEffect(() => {
    const collPath = getCollectionPath(collectionName);
    const q = query(collection(firebase.db, collPath), orderBy('label'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      let options = [];
      querySnapshot.forEach((docSnap) => {
        let { label } = docSnap.data();
        // Use doc.id as value since that's what gets stored in track documents
        options.push({ label, value: docSnap.id });
      });
      setOptions([...options]);
    });
    return () => {
      unsubscribe();
    };
  }, [collectionName, firebase, getCollectionPath]);

  const handleFieldValueChange = (event) => {
    setSelectedFieldValue(event.target.value);
  };

  const handleSelectedFieldChange = (data) => {
    if (data) {
      setSelectedFieldId(data.value);
      setSelectedFieldValue(data.label);
    } else {
      setIsEditing(false);
      setSelectedFieldId(null);
      setSelectedFieldValue('');
    }
  };

  const submitForm = (event) => {
    event.preventDefault();
  };

  // Helper to show toast
  const showToast = (message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: '' }), 3000);
  };

  const deleteField = async (id) => {
    setShowProcessModal(true);
    setProcessStatus('Deleting field and updating tracks...');

    // Find the label of the field being deleted (for tag mapping deletion)
    const fieldToDelete = options.find(option => option.value === id);
    const fieldLabel = fieldToDelete?.label;

    // Get the field collection path
    const fieldCollPath = getCollectionPath(collectionName);

    // Determine which audiometa collections to update
    let audioMetaPaths = [];

    if (isGlobalCollection(collectionName)) {
      // For global fields, update audiometa in ALL organizations
      setProcessStatus('Fetching all organizations...');
      const orgsSnapshot = await getDocs(collection(firebase.db, ORGANIZATIONSPATH));
      audioMetaPaths = orgsSnapshot.docs.map(orgDoc =>
        getOrgCollectionPath(orgDoc.id, 'audiometa')
      );
      setProcessStatus(`Removing field from tracks across ${audioMetaPaths.length} organizations...`);
    } else {
      // For org-scoped fields, only update current org's audiometa
      const audioMetaPath = getCollectionPath('audiometa');
      audioMetaPaths = [audioMetaPath];
    }

    try {
      let totalUpdated = 0;

      for (const audioMetaPath of audioMetaPaths) {
        const colRef = collection(firebase.db, audioMetaPath);
        const snapshot = await getDocs(colRef);

        if (snapshot.docs.length === 0) continue;

        const batch = writeBatch(firebase.db);
        let batchCount = 0;

        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          const docToUpdate = doc(firebase.db, audioMetaPath, docSnap.id);
          if (type === 'multi') {
            const fieldArray = data[keyName];
            if (Array.isArray(fieldArray)) {
              const hasField = fieldArray.some(item => item.value === id);
              if (hasField) {
                const newArray = fieldArray.filter(item => item.value !== id);
                batch.update(docToUpdate, { [keyName]: newArray });
                batchCount++;
              }
            }
          } else {
            // For single fields, clear the field if it matches the id
            const fieldObj = data[keyName];
            if (fieldObj && fieldObj.value === id) {
              batch.update(docToUpdate, { [keyName]: '' });
              batchCount++;
            }
          }
        });

        if (batchCount > 0) {
          await batch.commit();
          totalUpdated += batchCount;
        }
      }

      // For global fields that appear in licenses/license requests, update those too
      const licenseFields = ['flightingPlatform', 'territories', 'client', 'agency'];
      let licensesUpdated = 0;
      let requestsUpdated = 0;

      if (isGlobalCollection(collectionName) && licenseFields.includes(keyName)) {
        setProcessStatus('Removing field from licenses and license requests...');

        // Use collection group query to get ALL licenses across all orgs in one query
        const licensesQuery = query(collectionGroup(firebase.db, 'licenses'));
        const allLicensesSnapshot = await getDocs(licensesQuery);

        if (allLicensesSnapshot.docs.length > 0) {
          const licenseBatch = writeBatch(firebase.db);
          let licenseBatchCount = 0;

          for (const licenseDoc of allLicensesSnapshot.docs) {
            const licenseData = licenseDoc.data();
            if (type === 'multi') {
              const fieldArray = licenseData[keyName];
              if (Array.isArray(fieldArray)) {
                const hasField = fieldArray.some(item => item.value === id);
                if (hasField) {
                  const newArray = fieldArray.filter(item => item.value !== id);
                  licenseBatch.update(licenseDoc.ref, { [keyName]: newArray });
                  licenseBatchCount++;
                  if (licenseBatchCount >= 400) {
                    await licenseBatch.commit();
                    licensesUpdated += licenseBatchCount;
                    licenseBatchCount = 0;
                  }
                }
              }
            } else {
              const fieldObj = licenseData[keyName];
              if (fieldObj && fieldObj.value === id) {
                licenseBatch.update(licenseDoc.ref, { [keyName]: null });
                licenseBatchCount++;
                if (licenseBatchCount >= 400) {
                  await licenseBatch.commit();
                  licensesUpdated += licenseBatchCount;
                  licenseBatchCount = 0;
                }
              }
            }
          }

          if (licenseBatchCount > 0) {
            await licenseBatch.commit();
            licensesUpdated += licenseBatchCount;
          }
        }

        // Use collection group query to get ALL license requests across all orgs
        const requestsQuery = query(collectionGroup(firebase.db, 'licenseRequests'));
        const allRequestsSnapshot = await getDocs(requestsQuery);

        if (allRequestsSnapshot.docs.length > 0) {
          const requestBatch = writeBatch(firebase.db);
          let requestBatchCount = 0;

          for (const requestDoc of allRequestsSnapshot.docs) {
            const requestData = requestDoc.data();
            if (type === 'multi') {
              const fieldArray = requestData[keyName];
              if (Array.isArray(fieldArray)) {
                const hasField = fieldArray.some(item => item.value === id);
                if (hasField) {
                  const newArray = fieldArray.filter(item => item.value !== id);
                  requestBatch.update(requestDoc.ref, { [keyName]: newArray });
                  requestBatchCount++;
                  if (requestBatchCount >= 400) {
                    await requestBatch.commit();
                    requestsUpdated += requestBatchCount;
                    requestBatchCount = 0;
                  }
                }
              }
            } else {
              const fieldObj = requestData[keyName];
              if (fieldObj && fieldObj.value === id) {
                requestBatch.update(requestDoc.ref, { [keyName]: null });
                requestBatchCount++;
                if (requestBatchCount >= 400) {
                  await requestBatch.commit();
                  requestsUpdated += requestBatchCount;
                  requestBatchCount = 0;
                }
              }
            }
          }

          if (requestBatchCount > 0) {
            await requestBatch.commit();
            requestsUpdated += requestBatchCount;
          }
        }
      }

      // Delete the field document from its collection (separate batch)
      const deleteBatch = writeBatch(firebase.db);
      const docToDelete = doc(firebase.db, fieldCollPath, id);
      deleteBatch.delete(docToDelete);
      await deleteBatch.commit();

      // If this is a tag field being deleted, also delete its mapping
      if (collectionName === 'tags' && fieldLabel) {
        console.log(`🏷️ Deleting tag mapping for deleted tag: "${fieldLabel}"`);
        await tagMappingService.deleteTagMapping(fieldLabel);
      }

      setShowProcessModal(false);
      if (licensesUpdated > 0 || requestsUpdated > 0) {
        showToast(`Field deleted! (${totalUpdated} tracks, ${licensesUpdated} licenses, ${requestsUpdated} requests)`);
      } else {
        showToast(`Field deleted successfully! (${totalUpdated} tracks updated)`);
      }
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      setShowProcessModal(false);
      console.error('Error deleting field:', err);
    }
  };

  const saveEditedField = async ({ selectedFieldId, selectedFieldValue }) => {
    try {
      await saveEditedFieldLabel({ selectedFieldId, selectedFieldValue });
      await updateReferencesWithEditedField({
        selectedFieldId,
        selectedFieldValue,
      });
    } catch (err) {}
  };

  const updateReferencesWithEditedField = async ({
    selectedFieldId,
    selectedFieldValue,
  }) => {
    setShowProcessModal(true);
    setProcessStatus('Updating field label in all tracks...');

    // Determine which audiometa collections to update
    let audioMetaPaths = [];

    if (isGlobalCollection(collectionName)) {
      // For global fields (tags, genre, bpm, key, agency, client, etc.),
      // we need to update audiometa in ALL organizations
      setProcessStatus('Fetching all organizations...');
      const orgsSnapshot = await getDocs(collection(firebase.db, ORGANIZATIONSPATH));
      audioMetaPaths = orgsSnapshot.docs.map(orgDoc =>
        getOrgCollectionPath(orgDoc.id, 'audiometa')
      );
      setProcessStatus(`Updating field label across ${audioMetaPaths.length} organizations...`);
    } else {
      // For org-scoped fields, only update current org's audiometa
      const audioMetaPath = getCollectionPath('audiometa');
      audioMetaPaths = [audioMetaPath];
    }

    try {
      let totalUpdated = 0;

      for (const audioMetaPath of audioMetaPaths) {
        const colRef = collection(firebase.db, audioMetaPath);
        const snapshot = await getDocs(colRef);

        if (snapshot.docs.length === 0) continue;

        const batch = writeBatch(firebase.db);
        let batchCount = 0;

        for (const docSnap of snapshot.docs) {
          if (type === 'multi') {
            const fieldArray = docSnap.data()[keyName];
            if (Array.isArray(fieldArray)) {
              let updated = false;
              const newArray = fieldArray.map(item => {
                if (item.value === selectedFieldId) {
                  updated = true;
                  // Preserve all existing fields (like split for composers), just update the label
                  return { ...item, label: selectedFieldValue };
                }
                return item;
              });
              if (updated) {
                const docToUpdate = doc(firebase.db, audioMetaPath, docSnap.id);
                batch.update(docToUpdate, { [keyName]: newArray });
                batchCount++;
              }
            }
          } else {
            // For single fields (object with label and value), update if value matches
            const fieldObj = docSnap.data()[keyName];
            if (fieldObj && fieldObj.value === selectedFieldId) {
              const docToUpdate = doc(firebase.db, audioMetaPath, docSnap.id);
              batch.update(docToUpdate, {
                [keyName]: { label: selectedFieldValue, value: fieldObj.value }
              });
              batchCount++;
            }
          }
        }

        if (batchCount > 0) {
          await batch.commit();
          totalUpdated += batchCount;
        }
      }

      // For global fields that appear in licenses/license requests, update those too
      const licenseFields = ['flightingPlatform', 'territories', 'client', 'agency'];
      if (isGlobalCollection(collectionName) && licenseFields.includes(keyName)) {
        setProcessStatus('Updating field label in licenses and license requests...');

        let licensesUpdated = 0;
        let requestsUpdated = 0;

        // Use collection group query to get ALL licenses across all orgs in one query
        const licensesQuery = query(collectionGroup(firebase.db, 'licenses'));
        const allLicensesSnapshot = await getDocs(licensesQuery);

        if (allLicensesSnapshot.docs.length > 0) {
          const licenseBatch = writeBatch(firebase.db);
          let licenseBatchCount = 0;

          for (const licenseDoc of allLicensesSnapshot.docs) {
            const licenseData = licenseDoc.data();
            if (type === 'multi') {
              const fieldArray = licenseData[keyName];
              if (Array.isArray(fieldArray)) {
                let updated = false;
                const newArray = fieldArray.map(item => {
                  if (item.value === selectedFieldId) {
                    updated = true;
                    return { ...item, label: selectedFieldValue };
                  }
                  return item;
                });
                if (updated) {
                  licenseBatch.update(licenseDoc.ref, { [keyName]: newArray });
                  licenseBatchCount++;
                  // Firestore batch limit is 500
                  if (licenseBatchCount >= 400) {
                    await licenseBatch.commit();
                    licensesUpdated += licenseBatchCount;
                    licenseBatchCount = 0;
                  }
                }
              }
            } else {
              const fieldObj = licenseData[keyName];
              if (fieldObj && fieldObj.value === selectedFieldId) {
                licenseBatch.update(licenseDoc.ref, {
                  [keyName]: { label: selectedFieldValue, value: fieldObj.value }
                });
                licenseBatchCount++;
                if (licenseBatchCount >= 400) {
                  await licenseBatch.commit();
                  licensesUpdated += licenseBatchCount;
                  licenseBatchCount = 0;
                }
              }
            }
          }

          if (licenseBatchCount > 0) {
            await licenseBatch.commit();
            licensesUpdated += licenseBatchCount;
          }
        }

        // Use collection group query to get ALL license requests across all orgs
        const requestsQuery = query(collectionGroup(firebase.db, 'licenseRequests'));
        const allRequestsSnapshot = await getDocs(requestsQuery);

        if (allRequestsSnapshot.docs.length > 0) {
          const requestBatch = writeBatch(firebase.db);
          let requestBatchCount = 0;

          for (const requestDoc of allRequestsSnapshot.docs) {
            const requestData = requestDoc.data();
            if (type === 'multi') {
              const fieldArray = requestData[keyName];
              if (Array.isArray(fieldArray)) {
                let updated = false;
                const newArray = fieldArray.map(item => {
                  if (item.value === selectedFieldId) {
                    updated = true;
                    return { ...item, label: selectedFieldValue };
                  }
                  return item;
                });
                if (updated) {
                  requestBatch.update(requestDoc.ref, { [keyName]: newArray });
                  requestBatchCount++;
                  if (requestBatchCount >= 400) {
                    await requestBatch.commit();
                    requestsUpdated += requestBatchCount;
                    requestBatchCount = 0;
                  }
                }
              }
            } else {
              const fieldObj = requestData[keyName];
              if (fieldObj && fieldObj.value === selectedFieldId) {
                requestBatch.update(requestDoc.ref, {
                  [keyName]: { label: selectedFieldValue, value: fieldObj.value }
                });
                requestBatchCount++;
                if (requestBatchCount >= 400) {
                  await requestBatch.commit();
                  requestsUpdated += requestBatchCount;
                  requestBatchCount = 0;
                }
              }
            }
          }

          if (requestBatchCount > 0) {
            await requestBatch.commit();
            requestsUpdated += requestBatchCount;
          }
        }

        setShowProcessModal(false);
        showToast(`Field updated! (${totalUpdated} tracks, ${licensesUpdated} licenses, ${requestsUpdated} requests)`);
        setTimeout(() => window.location.reload(), 1000);
      } else {
        setShowProcessModal(false);
        showToast(`Field label updated successfully! (${totalUpdated} tracks updated)`);
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (err) {
      setShowProcessModal(false);
      console.error('Error updating field references:', err);
    }
  };

  const saveEditedFieldLabel = async ({
    selectedFieldId,
    selectedFieldValue,
  }) => {
    try {
      const fieldCollPath = getCollectionPath(collectionName);
      await updateDoc(doc(firebase.db, fieldCollPath, selectedFieldId), { label: selectedFieldValue });
    } catch (err) {
    }
  };

  return (
    <>
      {toast.show && (
        <div className="toast-container">
          <div className="alert alert-success" style={{ backgroundColor: 'var(--button-success)', color: '#fff', border: 'none' }}>
            <strong>{toast.message}</strong>
          </div>
        </div>
      )}
      {showProcessModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ background: '#fff', padding: 32, borderRadius: 12, boxShadow: '0 2px 12px #0008', minWidth: 280, textAlign: 'center' }}>
            <h5 style={{ marginBottom: 16 }}>{processStatus || 'Processing...'}</h5>
            <div style={{ width: '100%', background: '#eee', borderRadius: 8, height: 12, marginBottom: 16, overflow: 'hidden' }}>
              <div className="process-bar-animated" style={{ width: '40%', background: 'var(--accent-1-color, #007bff)', height: '100%', borderRadius: 8, animation: 'processBarAnim 1s linear infinite' }}></div>
            </div>
            <div style={{ fontSize: 13, color: '#888' }}>
              This may take a while for large collections.
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes processBarAnim {
          0% { margin-left: -40%; }
          100% { margin-left: 100%; }
        }
        .process-bar-animated {
          position: relative;
        }
      `}</style>
      <Form onSubmit={submitForm}>
        <FormGroup className="border-bottom pb-4">
          <h3 className="mb-3 mt-3">Field</h3>
          <div className="d-flex flex-row align-items-center w-50">
            <Select
              className="text-dark flex-grow-1"
              isClearable
              options={options}
              onChange={handleSelectedFieldChange}
            />
            <Button
              disabled={!selectedFieldValue}
              onClick={() => {
                setIsEditing(true);
              }}
              className="ml-2 btn-round btn-warning-custom"
            >
              <span>Edit</span>
            </Button>
            <ModalConsumer>
              {({ showModal }) => (
                <Button
                  onClick={() => {
                    showModal(GenericConfirmModal, {
                      isOpen: true,
                      title: 'Delete Field',
                      body: `Are you sure you want to delete ${selectedFieldValue}?`,
                      callback: () => {
                        deleteField(selectedFieldId);
                      },
                    });
                  }}
                  disabled={!selectedFieldValue}
                  className="ml-2 btn-round btn-primary-custom"
                >
                  <span>Delete</span>
                </Button>
              )}
            </ModalConsumer>
          </div>
        </FormGroup>
        {selectedFieldValue && isEditing && (
          <FormGroup className="mt-4">
            <div className="d-flex flex-row align-items-center">
              <Input
                onChange={handleFieldValueChange}
                value={selectedFieldValue}
                name="fieldValue"
                type="text"
                style={{ margin: 0 }}
              />
              <ModalConsumer>
                {({ showModal }) => (
                  <Button
                    disabled={!selectedFieldValue}
                    className="ml-2 btn-round btn-danger-custom btn-save-wide"
                    onClick={() => {
                      showModal(GenericConfirmModal, {
                        isOpen: true,
                        title: 'Save Edited Field',
                        body: `Are you sure you want to save ${selectedFieldValue}?`,
                        callback: () => {
                          saveEditedField({
                            selectedFieldId,
                            selectedFieldValue,
                          });
                        },
                      });
                    }}
                  >
                    <span>Save</span>
                  </Button>
                )}
              </ModalConsumer>
            </div>
          </FormGroup>
        )}
      </Form>
    </>
  );
};

export default withFirebase(FieldEditor);
